import React, { useCallback, useEffect, useState } from 'react';
import {
  API_ADD_CLIENT_FOR_DISBURSEMENT,
  API_ADD_CLIENTS_FOR_DISBURSEMENT_BULK,
  API_BRANCHES,
  API_CENTERS,
  API_DELETE_CLIENT_FOR_DISBURSEMENT,
  API_DISBURSEMENT_CLIENT_HISTORY,
  API_GET_CLIENTS,
  API_GET_LOANS,
  API_UPDATE_CLIENT_FOR_DISBURSEMENT,
} from '../Config/ApiUrlConstant';
import { apiJson } from '../utils/apiClient';
import SuccessModal from '../Common/SuccessModal';
import { confirmAction } from '../utils/confirmAction';

const emptyForm = { branch_id: '', center_id: '', client_id: '', demand_loan_id: '', approve_loan_id: '', remarks: '' };

const DisbursementClientQueue = () => {
  const [data, setData] = useState(emptyForm);
  const [branches, setBranches] = useState([]);
  const [centers, setCenters] = useState([]);
  const [clients, setClients] = useState([]);
  const [loans, setLoans] = useState([]);
  const [pending, setPending] = useState([]);
  const [selectedClientIds, setSelectedClientIds] = useState([]);
  const [editId, setEditId] = useState('');
  const [bulkMode, setBulkMode] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [message, setMessage] = useState('');

  const request = useCallback(async (url, body = {}) => {
    const { response, data: responseData } = await apiJson(url, { method: 'POST', body: JSON.stringify(body) });
    if (!response.ok || responseData?.status === false) throw new Error(responseData?.message || 'Unable to load data.');
    return responseData?.data || [];
  }, []);

  const loadPending = useCallback(async () => {
    const queue = await request(API_DISBURSEMENT_CLIENT_HISTORY, { status: 'pending' });
    setPending(queue);
  }, [request]);

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setIsLoading(true);
        setErrorMessage('');
        const [branchRows, loanRows] = await Promise.all([
          request(API_BRANCHES),
          request(API_GET_LOANS, { is_all: true, include_inactive: true }),
          loadPending(),
        ]);
        setBranches(branchRows);
        setLoans(loanRows);
      } catch (error) {
        setErrorMessage(error.message || 'Unable to load disbursement queue.');
      } finally {
        setIsLoading(false);
      }
    };
    void loadInitialData();
  }, [loadPending, request]);

  const handleChange = async (field, value) => {
    try {
      setErrorMessage('');
      if (field === 'branch_id') {
        setData({ ...emptyForm, branch_id: value });
        setCenters(value ? await request(API_CENTERS, { is_all: false, branch_id: value }) : []);
        setClients([]);
        setSelectedClientIds([]);
        return;
      }
      if (field === 'center_id') {
        setData({ ...data, center_id: value, client_id: '' });
        setClients(value ? await request(API_GET_CLIENTS, { is_all_list: true, center_id: value }) : []);
        setSelectedClientIds([]);
        return;
      }
      setData({ ...data, [field]: value });
    } catch (error) {
      setErrorMessage(error.message || 'Unable to load selection options.');
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    const isBulk = bulkMode && !editId;
    if (isBulk && !selectedClientIds.length) {
      setErrorMessage('Select at least one client for bulk add.');
      return;
    }
    const payload = isBulk ? { clients: selectedClientIds.map((clientId) => ({ ...data, client_id: clientId })) } : editId ? { ...data, id: editId } : data;
    const url = isBulk ? API_ADD_CLIENTS_FOR_DISBURSEMENT_BULK : editId ? API_UPDATE_CLIENT_FOR_DISBURSEMENT : API_ADD_CLIENT_FOR_DISBURSEMENT;
    if (!confirmAction(isBulk ? `add ${selectedClientIds.length} clients for disbursement` : editId ? 'update this disbursement queue entry' : 'add this client for disbursement')) return;
    try {
      setIsSaving(true);
      setErrorMessage('');
      const { responseData } = await (async () => {
        const { response, data: responseData } = await apiJson(url, { method: 'POST', body: JSON.stringify(payload) });
        if (!response.ok || !responseData?.status) throw new Error(responseData?.message || 'Unable to save client.');
        return { responseData };
      })();
      setMessage(responseData.message || 'Client saved for disbursement.');
      setEditId('');
      setSelectedClientIds([]);
      setData({ ...emptyForm, branch_id: data.branch_id, center_id: data.center_id });
      await loadPending();
    } catch (error) {
      setErrorMessage(error.message || 'Unable to save client.');
    } finally {
      setIsSaving(false);
    }
  };

  const edit = async (entry) => {
    try {
      setErrorMessage('');
      setBulkMode(false);
      setEditId(entry.id);
      setData({ ...emptyForm, ...entry });
      const [centerRows, clientRows] = await Promise.all([
        request(API_CENTERS, { is_all: false, branch_id: entry.branch_id }),
        request(API_GET_CLIENTS, { is_all_list: true, center_id: entry.center_id }),
      ]);
      setCenters(centerRows);
      setClients(clientRows);
    } catch (error) {
      setErrorMessage(error.message || 'Unable to load queue entry.');
    }
  };

  const remove = async (entry) => {
    if (!confirmAction('archive this pending queue entry')) return;
    try {
      setErrorMessage('');
      await request(API_DELETE_CLIENT_FOR_DISBURSEMENT, { id: entry.id, branch_id: entry.branch_id });
      setMessage('Pending queue entry archived.');
      await loadPending();
    } catch (error) {
      setErrorMessage(error.message || 'Unable to delete queue entry.');
    }
  };

  return <div className="container my-5">
    <div className="d-flex justify-content-between align-items-center"><h2>{editId ? 'Edit' : bulkMode ? 'Bulk Add' : 'Add'} Client For Disbursement</h2>{!editId && <button type="button" className="btn btn-outline-primary" onClick={() => { setBulkMode(!bulkMode); setSelectedClientIds([]); }}>{bulkMode ? 'Use Single Add' : 'Use Bulk Add'}</button>}</div>
    {errorMessage ? <div className="alert alert-danger mt-3">{errorMessage}</div> : null}
    <form onSubmit={submit} className="row g-3">
      <div className="col-md-6"><label>Branch *</label><select className="form-control" value={data.branch_id} onChange={(event) => handleChange('branch_id', event.target.value)} required disabled={isLoading}><option value="">Select branch</option>{branches.map((branch) => <option key={branch.id} value={branch.id}>{branch.branch_name}</option>)}</select></div>
      <div className="col-md-6"><label>Center *</label><select className="form-control" value={data.center_id} onChange={(event) => handleChange('center_id', event.target.value)} required disabled={!data.branch_id || isLoading}><option value="">Select center</option>{centers.map((center) => <option key={center.id} value={center.id}>{center.center_name}</option>)}</select></div>
      <div className="col-md-6"><label>{bulkMode && !editId ? 'Clients *' : 'Client *'}</label>{bulkMode && !editId ? <select multiple className="form-control" value={selectedClientIds} onChange={(event) => setSelectedClientIds(Array.from(event.target.selectedOptions, (option) => option.value))} required>{clients.map((client) => <option key={client.id} value={client.id}>{client.client_no} - {client.client_name}</option>)}</select> : <select className="form-control" value={data.client_id} onChange={(event) => handleChange('client_id', event.target.value)} required disabled={!data.center_id}><option value="">Select client</option>{clients.map((client) => <option key={client.id} value={client.id}>{client.client_no} - {client.client_name}</option>)}</select>}</div>
      <div className="col-md-3"><label>Demand *</label><select className="form-control" value={data.demand_loan_id} onChange={(event) => handleChange('demand_loan_id', event.target.value)} required><option value="">Select</option>{loans.map((loan) => <option key={loan.id} value={loan.id}>{loan.loan_amt}</option>)}</select></div>
      <div className="col-md-3"><label>Approved *</label><select className="form-control" value={data.approve_loan_id} onChange={(event) => handleChange('approve_loan_id', event.target.value)} required><option value="">Select</option>{loans.map((loan) => <option key={loan.id} value={loan.id}>{loan.loan_amt}</option>)}</select></div>
      <div className="col-12"><textarea className="form-control" placeholder="Remarks" value={data.remarks} onChange={(event) => handleChange('remarks', event.target.value)} /><button className="btn btn-primary mt-2" disabled={isSaving}>{isSaving ? 'Saving...' : bulkMode && !editId ? 'Add Selected Clients' : 'Save'}</button></div>
    </form>
    <h4 className="mt-4">Pending Queue</h4>
    <div className="table-responsive data-table-shell"><table className="table table-bordered data-table"><thead><tr><th>Client No</th><th>Client Name</th><th>Branch</th><th>Center</th><th>Demand Loan</th><th>Approved Loan</th><th>Remarks</th><th>Queue Date</th><th>Action</th></tr></thead><tbody>{pending.length ? pending.map((entry) => <tr key={entry.id}><td>{entry.client_no}</td><td>{entry.client_name}</td><td>{entry.branch_name}</td><td>{entry.center_name}</td><td>{entry.demand_loan_amt}</td><td>{entry.approve_loan_amt}</td><td>{entry.remarks || '-'}</td><td>{entry.created_at ? new Date(entry.created_at).toLocaleDateString('en-GB').replaceAll('/', '-') : '-'}</td><td><button type="button" className="btn btn-sm btn-warning me-1" onClick={() => edit(entry)}>Edit</button><button type="button" className="btn btn-sm btn-danger" onClick={() => remove(entry)}>Delete</button></td></tr>) : <tr><td colSpan="9" className="text-center">{isLoading ? 'Loading queue...' : 'No pending clients.'}</td></tr>}</tbody></table></div>
    {message ? <SuccessModal message={message} onClose={() => setMessage('')} /> : null}
  </div>;
};

export default DisbursementClientQueue;
